import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Divider, Empty, Spin, Typography } from 'antd';
import { useGetGroupedMaterialReservationsQuery } from '../../../../redux/api/material';
import SawCutCard from '../SawCutCard/index.tsx';
import { GroupedMaterialReservation, QueueSummary } from '../types';
import { DEFAULT_PRIORITIZATION, DEFAULT_QUEUE_VIEW } from '../queueClassification';
import { useNavigate } from 'react-router-dom';
import { TABLET_CARD_GRID_STYLE } from '../../tabletStyles';

interface IProps {
	productionMethod?: string;
	buildingId?: string | null;
	onReadyChange?: (ready: boolean) => void;
	onSummaryChange?: (summary?: QueueSummary) => void;
}

const SawCutQueueTable: React.FC<IProps> = ({
	productionMethod,
	buildingId,
	onReadyChange,
	onSummaryChange,
}: IProps) => {
	const [dataSource, setDataSource] = useState<GroupedMaterialReservation[]>([]);
	const [showMoreButton, setShowMoreButton] = useState(true);
	const [pageNumber, setPageNumber] = useState(1);
	const [pagesToFetch, setPagesToFetch] = useState<number[]>([]);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const history = useNavigate();

	const queryParams = new URLSearchParams(location.search);
	const search = queryParams.get('search') || '';
	const urlPage = Number(queryParams.get('page') || 1);
	const materialCutStatus = queryParams.get('group_cut_status') || 'Pending';
	const ordering = queryParams.get('ordering') || '';
	const queueView = queryParams.get('queue_view') || DEFAULT_QUEUE_VIEW;
	const prioritization =
		queryParams.get('prioritization') || DEFAULT_PRIORITIZATION;

	const prevStatusRef = useRef(materialCutStatus);
	const [hideDataUntilFetchComplete, setHideDataUntilFetchComplete] =
		useState(false);

	const currentPageToFetch =
		pagesToFetch.length > 0 ? pagesToFetch[0] : pageNumber;

	const cuttingQuery = useMemo(() => {
		const q: Record<string, string | number> = {
			page_size: 10,
			search,
			page: currentPageToFetch,
			group_cut_status: materialCutStatus,
			queue_view: queueView,
			prioritization,
		};
		if (productionMethod) {
			q.partsbadger_production_method = productionMethod;
		}
		if (buildingId) {
			q.building = buildingId;
		}
		if (ordering) {
			q.ordering = ordering;
		}
		return q;
	}, [
		search,
		materialCutStatus,
		currentPageToFetch,
		productionMethod,
		buildingId,
		ordering,
		queueView,
		prioritization,
	]);

	const { data, isFetching } = useGetGroupedMaterialReservationsQuery(
		cuttingQuery,
		{ refetchOnFocus: true, refetchOnMountOrArgChange: true }
	);

	const isReady = !isFetching && data !== undefined;

	useEffect(() => {
		onReadyChange?.(isReady);
	}, [isReady, onReadyChange]);

	// When switching cut-status tabs, avoid appending stale results before the new fetch finishes
	useEffect(() => {
		if (prevStatusRef.current !== materialCutStatus) {
			prevStatusRef.current = materialCutStatus;
			setHideDataUntilFetchComplete(true);
		}
	}, [materialCutStatus]);

	useEffect(() => {
		if (!isFetching && hideDataUntilFetchComplete) {
			setHideDataUntilFetchComplete(false);
		}
	}, [isFetching, hideDataUntilFetchComplete]);

	// On initial mount: if URL has page > 1, load all pages to restore scroll depth
	useEffect(() => {
		if (isInitialLoad && urlPage > 1) {
			const pages = Array.from({ length: urlPage }, (_, i) => i + 1);
			setPagesToFetch(pages);
			setPageNumber(urlPage);
		}
		setIsInitialLoad(false);
	}, []);

	// Reset list when any filter or sort changes
	const prevFiltersRef = useRef({
		buildingId,
		materialCutStatus,
		search,
		productionMethod,
		ordering,
		queueView,
		prioritization,
	});

	useEffect(() => {
		const prev = prevFiltersRef.current;
		if (
			prev.buildingId !== buildingId ||
			prev.materialCutStatus !== materialCutStatus ||
			prev.search !== search ||
			prev.productionMethod !== productionMethod ||
			prev.ordering !== ordering ||
			prev.queueView !== queueView ||
			prev.prioritization !== prioritization
		) {
			setDataSource([]);
			setPageNumber(1);
			setShowMoreButton(true);
			setPagesToFetch([]);
			prevFiltersRef.current = {
				buildingId,
				materialCutStatus,
				search,
				productionMethod,
				ordering,
				queueView,
				prioritization,
			};
			const params = new URLSearchParams(location.search);
			params.set('page', '1');
			history({ search: params.toString() }, { replace: true });
		}
	}, [
		buildingId,
		materialCutStatus,
		search,
		productionMethod,
		ordering,
		queueView,
		prioritization,
	]);

	useEffect(() => {
		if (data?.queue_summary) {
			onSummaryChange?.(data.queue_summary as QueueSummary);
		}
	}, [data, onSummaryChange]);

	useEffect(() => {
		if (!data?.results) return;
		if (hideDataUntilFetchComplete) return;

		const results = data.results as GroupedMaterialReservation[];

		if (currentPageToFetch === 1) {
			setDataSource(results);
		} else {
			setDataSource((prev) => {
				const existingKeys = new Set(prev.map((item) => item.key));
				const newResults = results.filter((r) => !existingKeys.has(r.key));
				if (newResults.length === 0) return prev;
				return [...prev, ...newResults];
			});
		}

		if (pagesToFetch.length > 0) {
			if (!data.next || pagesToFetch.length === 1) {
				setPagesToFetch([]);
				setShowMoreButton(!!data.next);
			} else {
				setPagesToFetch((prev) => prev.slice(1));
			}
		} else {
			setShowMoreButton(!!data.next);
		}
	}, [data, hideDataUntilFetchComplete]);

	const onLoadMore = () => {
		const nextPage = pageNumber + 1;
		setPageNumber(nextPage);
		const params = new URLSearchParams(location.search);
		params.set('page', String(nextPage));
		history({ search: params.toString() });
	};

	const displayCards = hideDataUntilFetchComplete ? [] : dataSource;

	// Past the classification cap the server returns every row unclassified, and
	// it applies neither the view filter nor the readiness order.
	//
	// The filter half is invisible on "All", because asking for everything and
	// getting everything agree. The order half is not: readiness ordering is the
	// default, so "Production first" stays lit over raw due-date order in the one
	// combination the queue opens on — which used to be the only unflagged one.
	// An explicit `ordering` wins server-side, so readiness was never in play
	// there and there is nothing to report.
	const classificationSkipped = data?.queue_classification_applied === false;
	const viewNotApplied = classificationSkipped && queueView !== 'All';
	const orderNotApplied =
		classificationSkipped && !ordering && prioritization === 'readiness';
	const nothingApplied = viewNotApplied || orderNotApplied;
	const notAppliedDescription = [
		viewNotApplied
			? 'they are not sorted into ready, blocked and needs review'
			: null,
		orderNotApplied ? 'they are in due-date order, not production order' : null,
	]
		.filter(Boolean)
		.join(', and ');

	// An empty ready list is not the same as an empty queue. The queue opens on
	// "All", so this only bites once the operator has filtered to a class: there
	// "No Items To Cut" would read as "there is nothing to do" on a shift where
	// every job happens to be held.
	const summary = data?.queue_summary as QueueSummary | undefined;
	const elsewhere =
		queueView === 'Actionable' && summary
			? [
					{ label: 'blocked', count: summary.blocked },
					{ label: 'needing review', count: summary.needs_review },
				].filter((entry) => (entry.count ?? 0) > 0)
			: [];

	// Only after we have rows: show footer so "Load more" gives clear feedback (spinner) while fetching
	const showLoadMoreFooter = showMoreButton && displayCards.length > 0;
	const loadMoreBusy = isFetching && displayCards.length > 0;

	return (
		<>
			{nothingApplied ? (
				<Alert
					data-testid="fastlane-cut-queue-view-not-applied"
					type="warning"
					showIcon
					style={{ marginBottom: 12 }}
					message={
						viewNotApplied
							? 'Showing every job, not just this view'
							: 'Showing these jobs in due-date order'
					}
					description={`There are too many groups here to label them, so ${notAppliedDescription}. Narrow the search or the building to get the labels back.`}
				/>
			) : null}
			{isFetching && displayCards.length === 0 ? (
				<div style={{ textAlign: 'center', padding: 48 }}>
					<Spin size="large" />
				</div>
			) : displayCards.length === 0 ? (
				<Empty
					description={
						<div>
							<Typography.Title level={4}>
								{queueView === 'Actionable' && !search
									? 'Nothing ready to cut'
									: 'No Items To Cut'}
							</Typography.Title>
							<Typography.Text data-testid="fastlane-cut-queue-empty-reason">
								{search
									? `No results found for "${search}"`
									: elsewhere.length > 0
										? `Every job here is ${elsewhere
												.map((entry) => `${entry.count} ${entry.label}`)
												.join(' and ')}. Switch views above to see why.`
										: queueView !== 'All'
											? 'Nothing in this view right now'
											: 'No items to cut at this time'}
							</Typography.Text>
						</div>
					}
				/>
			) : (
				<div style={TABLET_CARD_GRID_STYLE}>
					{displayCards.map((record) => (
						<SawCutCard
							key={record.key}
							record={record}
						/>
					))}
				</div>
			)}

			{showLoadMoreFooter ? (
				<>
					<Divider style={{ margin: '24px 0 16px' }} plain>
						<Typography.Text type="secondary" style={{ fontSize: 13 }}>
							More in queue
						</Typography.Text>
					</Divider>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							gap: 8,
							paddingBottom: 8,
						}}
					>
						<Button
							size="large"
							onClick={onLoadMore}
							loading={loadMoreBusy}
							title="Loads the next page of groups (10 per page)"
						>
							Load more
						</Button>
					</div>
				</>
			) : null}
		</>
	);
};

export default SawCutQueueTable;
